import { LookupYesNoType } from '@/api/dtos';
import { AppSettings } from '@/AppSettings';
import EConsentChecklist from '@/components/consent/EConsentChecklist';
import EConsentPage from '@/components/consent/EConsentPage';
import EConsentSignatureFields from '@/components/consent/EConsentSignatureFields';
import EConsentSignatureUpdater from '@/components/consent/EConsentSignatureUpdater';
import EConsentTrialDetails from '@/components/consent/EConsentTrialDetails';
import { mainConsentEConsentRegistration } from '@/components/consent/eConsentFormRegistry';
import EConsentForm from '@/components/forms/EConsentForm';
import EConsentContext, { EConsentUserType } from '@/contexts/EConsentContext';
import { Box, Button, styled, Typography } from '@mui/material';
import { GroupedField, GroupedFields, IFormLabel, ILookup, InstitutionContext, ToggleButtonGroup } from '@ngt/opms';
import { ValidateOn } from '@ngt/forms-core';
import { FC, useContext } from 'react';

const Paragraph = styled(Typography)(({ theme }) => ({ paddingBottom: theme.spacing(1.5) }));
const SectionHeading = styled(Typography)(({ theme }) => ({
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1.5),
    fontWeight: 'bold',
    [theme.breakpoints.down('sm')]: { width: '80%' },
}));
const PicfButton = styled(Button)(({ theme }) => ({ padding: theme.spacing(1) }));

const fieldGrid = { size: { xs: 12, sm: 12, md: 5, lg: 4, xl: 4 } };
const containerGrid = { sx: { paddingBottom: 1.5, paddingTop: 1.5 } };

const consentLookups: ILookup[] = [
    {
        propertyName: 'participantAgreedToProvideBloodSample',
        items: [
            { id: LookupYesNoType.Yes, value: 'Yes' },
            { id: LookupYesNoType.No, value: 'No' },
        ],
    },
    {
        propertyName: 'participantAgreedToCompleteQuestionnaires',
        items: [
            { id: LookupYesNoType.Yes, value: 'Yes' },
            { id: LookupYesNoType.No, value: 'No' },
        ],
    },
];

const consentLabels: IFormLabel[] = [
    { propertyPath: 'participantAgreedToProvideBloodSample', detailedLabel: undefined, label: 'I agree to provide blood samples for the trial.' },
    { propertyPath: 'participantAgreedToCompleteQuestionnaires', detailedLabel: undefined, label: 'I agree to take part in completing Questionnaires for the trial.' },
    { propertyPath: 'patientName', detailedLabel: undefined, label: 'Name' },
    { propertyPath: 'datePatientSignedConsent', detailedLabel: undefined, label: 'Date' },
    { propertyPath: 'investigatorName', detailedLabel: undefined, label: 'Name' },
    { propertyPath: 'dateInvestigatorSignedConsent', detailedLabel: undefined, label: 'Date' },
];

const consentStatements = [
    'I freely agree to take part in this trial.',
    'I understand that I can stop taking part in the trial at any time.',
    'I have read, or have had read to me in a language that I understand, the information provided about this trial and understand what is involved, including the use of my personal information.',
    'I have had the opportunity to consider the information, ask questions and am satisfied with the answers I received.',
    'I give permission for my medical records to be accessed for the purposes of this trial.',
    'I understand that I may be asked to attend follow-up visits to allow collection of personal and health information about my health. Alternatively, the investigator/sponsor will request my permission to access my medical records for collection of follow-up personal and health information for research and analysis.',
    'I agree to the storage and use of tissue and blood samples and images taken from me for use as described in the relevant section of the Participant Information Sheet.',
    'I understand that BCT may in future request specific details about me from this institution (such as name, sex, date of birth, address) for use in data linkage. I understand that requests for data linkage will only occur after specific approvals are obtained, BCT will only give my identifiable information to the data custodian(s) for data linkage, and the data custodian(s) will de-identify my information before giving it to researchers.',
    'I understand that I will be given a signed copy of this document to keep.',
];

const MainConsentFields: FC = () => {
    const { userType } = useContext(EConsentContext);

    return (
        <EConsentForm
            requestTypeName={mainConsentEConsentRegistration.requestTypeName}
            disableHeading
            disablePaper
            labels={consentLabels}
            lookups={consentLookups}
            validateOn={ValidateOn.onChange}
            debug={AppSettings.debug}
            hideSubmit
            hideCancel
            submitAndReturnButtonText="Submit"
        >
            <EConsentSignatureUpdater />
            <GroupedFields ContainerProps={{ borderBottom: 'none !important', borderTop: 'none !important' }}>
                <SectionHeading>Consent to take part in the optional parts of this trial</SectionHeading>
                <GroupedField
                    propertyPath="participantAgreedToProvideBloodSample"
                    component={ToggleButtonGroup}
                    FieldGridProps={fieldGrid}
                    ContainerGridProps={containerGrid}
                    disabled={userType === EConsentUserType.Investigator}
                />
                <GroupedField
                    propertyPath="participantAgreedToCompleteQuestionnaires"
                    component={ToggleButtonGroup}
                    FieldGridProps={fieldGrid}
                    ContainerGridProps={containerGrid}
                    disabled={userType === EConsentUserType.Investigator}
                />
                <EConsentSignatureFields
                    investigatorStatement="I have explained the trial, its procedures and risks to the participant and I believe they have understood that explanation."
                />
            </GroupedFields>
        </EConsentForm>
    );
};

const MainConsentEConsentForm: FC = () => {
    const { principalInvestigator, picfDownloadUrl, picfVersion } = useContext(EConsentContext);
    const { data: institution } = useContext(InstitutionContext);

    return (
        <EConsentPage>
            <EConsentTrialDetails details={[
                { label: 'Short Name of Trial', value: 'BCT 2601 OPT-Pembro' },
                {
                    label: 'Full Name of Trial',
                    value: 'Optimizing adjuvant prescription of Pembrolizumab in patients with early-stage triple-negative breast cancer achieving pathologic complete response after standard neoadjuvant chemotherapy and pembrolizumab.',
                },
                { label: 'Principal Investigator', value: principalInvestigator },
                { label: 'Trial sponsor', value: 'Breast Cancer Trials' },
                { label: 'Site Name', value: institution?.name },
            ]} />

            <Paragraph>
                We, the trial team invite you to take part in a trial that wants to find out if treatment can be
                personalised for people, by safely giving them less treatment after breast cancer surgery.
                You have been invited to take part because:
            </Paragraph>
            <ul>
                <li><Paragraph>You have had treatment (chemotherapy + pembrolizumab) for early-stage triple negative breast cancer.</Paragraph></li>
                <li><Paragraph>Surgery found that <u>no cancer</u> is left in the breast.</Paragraph></li>
            </ul>
            <Paragraph>
                If you take part, you will be randomised (like flipping a coin) to have pembrolizumab for another
                six months OR not to have pembrolizumab and have regular check-ups (observation). After the
                6 months of treatment/observation, everyone will have follow up visits every 3 months for 1 year,
                every 3 months for 3 years, and then once per year for 6 years.
            </Paragraph>
            <Paragraph>
                Around 2,454 people around the world will take part in OPT-Pembro. This includes about 120 patients
                from 25 hospitals in Australia.
            </Paragraph>
            <Paragraph>
                Please click the following link to view the full Main Consent Participant Information and Consent Form.
                Please read this information and feel free to ask any questions. You can also talk to someone you trust,
                like a family member, mob, friend or your doctor. You can take some time to make up your mind. You get to
                decide if this trial is right for you.
            </Paragraph>

            {picfVersion && picfDownloadUrl && (
                <Box sx={{ textAlign: 'center', mt: 0.5 }}>
                    <PicfButton
                        color="primary"
                        variant="contained"
                        // @ts-expect-error MUI supports rendering Button as an anchor.
                        component="a"
                        href={picfDownloadUrl}
                        target="_blank"
                        rel="noopener"
                    >
                        {picfVersion}
                    </PicfButton>
                </Box>
            )}

            <SectionHeading>Consent to take part in this trial</SectionHeading>
            <EConsentChecklist items={consentStatements} />
            <MainConsentFields />
        </EConsentPage>
    );
};

export default MainConsentEConsentForm;
